/* eslint-disable arrow-body-style */
import React, { useEffect, useState } from "react";
import { Icon } from "@iconify/react";
import { useFormikContext } from "formik";
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { toast } from "react-toastify";
import {
  ButtonPrimary,
  ButtonPrimary1,
  HStack,
  VStack,
} from "../../../components/utils";
import { CTADropdown } from "../../../components/shared/Dropdown";
import {
  TransactionDetailsData,
  WorkflowStepConfig,
  WorkflowRequirement,
  WorkflowStepType,
  WORKFLOW_STEP_DEFAULT_LABELS,
} from "../../../types/Transaction";
import { saveTransactionDetails } from "../../../api/TransactionApisV2";
import WorkflowRequirementEditor from "./WorkflowRequirementEditor";
import {
  getWorkflowRequirementError,
  getWorkflowStepRequirements,
} from "./utils/workflowRequirements";

interface WorkflowOrderStepProps {
  onStepChange: (e?: number) => void;
  onBackClick: (step?: number) => void;
}

const FIXED_FIRST: WorkflowStepType[] = [
  WorkflowStepType.WILLINGNESS,
  WorkflowStepType.ADMIN_APPROVAL,
];
const FIXED_LAST: WorkflowStepType[] = [WorkflowStepType.SHARE_ALLOCATION];
const REQUIREMENT_CONFIGURABLE_STEPS = new Set<WorkflowStepType>([
  WorkflowStepType.WILLINGNESS,
]);
const CONFIGURE_REQUIREMENTS_ACTIONS = [{ name: "Configure requirements" }];
const CLOSE_REQUIREMENTS_ACTIONS = [{ name: "Close requirements" }];

function SortableStepItem({
  step,
  isFixed,
  description,
  stepNumber,
  requirementEditor,
}: {
  step: WorkflowStepConfig;
  isFixed: boolean;
  description: string;
  stepNumber: number;
  requirementEditor?: React.ReactNode;
}) {
  const [isRequirementsEditorOpen, setRequirementsEditorOpen] = useState(false);
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: step.type,
    disabled: isFixed,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 10 : 1,
  };

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`border rounded-lg ${
        isFixed ? "bg-gray-50 border-gray-200" : "bg-white border-gray-300"
      } ${isDragging ? "shadow-lg opacity-90 scale-[1.02]" : ""}`}
    >
      <HStack className="w-full items-center gap-3 px-4 py-3">
        <span className="w-6 h-6 rounded-full bg-gray-100 text-gray-500 text-xs font-semibold flex items-center justify-center shrink-0">
          {stepNumber}
        </span>

        {!isFixed ? (
          <button
            type="button"
            className="text-gray-300 hover:text-gray-500 cursor-grab active:cursor-grabbing shrink-0"
            {...attributes}
            {...listeners}
            title="Drag to reorder"
          >
            <Icon icon="mdi:drag-vertical" width={18} />
          </button>
        ) : (
          <Icon
            icon="mdi:lock-outline"
            width={16}
            className="text-gray-300 shrink-0"
          />
        )}

        <VStack className="min-w-0 flex-1 gap-1">
          <HStack className="flex-wrap items-center gap-2">
            <p className="text-sm font-semibold text-gray-800">
              {WORKFLOW_STEP_DEFAULT_LABELS[step.type]}
            </p>
            {requirementEditor && (
              <span className="rounded-sm bg-tertiaryBg px-2 py-1 text-xs text-secondaryText">
                {step.config?.requirements?.length ?? 0} configured
              </span>
            )}
          </HStack>
          <p className="text-xs text-gray-500">{description}</p>
        </VStack>
        {requirementEditor && (
          <CTADropdown
            className="shrink-0"
            actions={
              isRequirementsEditorOpen
                ? CLOSE_REQUIREMENTS_ACTIONS
                : CONFIGURE_REQUIREMENTS_ACTIONS
            }
            onAction={() => setRequirementsEditorOpen((open) => !open)}
            render={() => (
              <span
                className="flex h-10 w-10 items-center justify-center text-secondaryText"
                title={`${
                  step.label ?? WORKFLOW_STEP_DEFAULT_LABELS[step.type]
                } options`}
              >
                <Icon icon="mdi:dots-vertical" width={18} aria-hidden="true" />
                <span className="sr-only">
                  {step.label ?? WORKFLOW_STEP_DEFAULT_LABELS[step.type]}{" "}
                  options
                </span>
              </span>
            )}
          />
        )}
      </HStack>
      {isRequirementsEditorOpen && requirementEditor}
    </div>
  );
}

function deriveStepsFromMonetaryDetails(md: any): WorkflowStepConfig[] {
  if (md.workflowSteps && md.workflowSteps.length > 0) {
    return md.workflowSteps.map((step: WorkflowStepConfig) => {
      if (step.type === WorkflowStepType.ADMIN_APPROVAL) {
        return { ...step, enabled: !md.autoApproveExerciseRequests };
      }
      return step;
    });
  }
  return [
    { type: WorkflowStepType.WILLINGNESS, order: 1, enabled: true },
    {
      type: WorkflowStepType.ADMIN_APPROVAL,
      order: 2,
      enabled: !md.autoApproveExerciseRequests,
    },
    {
      type: WorkflowStepType.DOCUMENT_SIGNING,
      order: 3,
      enabled: !!md.generateExerciseLetters,
    },
    {
      type: WorkflowStepType.EXERCISE_PAYMENT,
      order: 4,
      enabled: !!md.transactionPaymentEnabled,
    },
    {
      type: WorkflowStepType.TAX_PAYMENT,
      order: 5,
      enabled: !!md.transactionPaymentEnabled && !!md.taxPaymentEnabled,
    },
    { type: WorkflowStepType.SHARE_ALLOCATION, order: 6, enabled: true },
  ];
}

function areWorkflowStepsEqual(
  a: WorkflowStepConfig[] = [],
  b: WorkflowStepConfig[] = []
) {
  if (a.length !== b.length) return false;
  for (let i = 0; i < a.length; i += 1) {
    if (
      a[i].type !== b[i].type ||
      a[i].order !== b[i].order ||
      a[i].enabled !== b[i].enabled ||
      JSON.stringify(a[i].config ?? {}) !== JSON.stringify(b[i].config ?? {})
    ) {
      return false;
    }
  }
  return true;
}

export default function WorkflowOrderStep({
  onStepChange,
  onBackClick,
}: WorkflowOrderStepProps) {
  const { values, setFieldValue } = useFormikContext<TransactionDetailsData>();
  const md = values.monetaryDetails;

  const [steps, setSteps] = useState<WorkflowStepConfig[]>(() =>
    deriveStepsFromMonetaryDetails(md)
  );

  // Keep ADMIN_APPROVAL in sync with autoApproveExerciseRequests, even for pre-saved workflowSteps.
  useEffect(() => {
    const derived = deriveStepsFromMonetaryDetails(md);
    if (!areWorkflowStepsEqual(steps, derived)) {
      setSteps(derived);
    }
    if (
      !areWorkflowStepsEqual(md.workflowSteps as WorkflowStepConfig[], derived)
    ) {
      setFieldValue("monetaryDetails.workflowSteps", derived);
    }
  }, [md.autoApproveExerciseRequests, md.workflowSteps, setFieldValue, steps]);

  const fixedFirstSet = new Set<WorkflowStepType>(FIXED_FIRST);
  const fixedLastSet = new Set<WorkflowStepType>(FIXED_LAST);

  // Only show enabled steps in the order they are stored
  const visibleSteps = steps
    .filter((s) => s.enabled)
    .sort((a, b) => a.order - b.order);

  const middleSteps = visibleSteps.filter(
    (s) => !fixedFirstSet.has(s.type) && !fixedLastSet.has(s.type)
  );
  const firstSteps = visibleSteps.filter((s) => fixedFirstSet.has(s.type));
  const lastSteps = visibleSteps.filter((s) => fixedLastSet.has(s.type));

  function applyNewMiddleOrder(newMiddle: WorkflowStepConfig[]) {
    const orderedVisible = [...firstSteps, ...newMiddle, ...lastSteps].map(
      (s, i) => ({ ...s, order: i + 1 })
    );
    const disabledSteps = steps
      .filter((s) => !s.enabled)
      .map((s, i) => ({ ...s, order: orderedVisible.length + i + 1 }));
    const allSteps = [...orderedVisible, ...disabledSteps];
    setSteps(allSteps);
    setFieldValue("monetaryDetails.workflowSteps", allSteps);
  }

  function updateRequirements(
    stepType: WorkflowStepType,
    requirements: WorkflowRequirement[]
  ) {
    const next = steps.map((step) =>
      step.type === stepType
        ? { ...step, config: { ...step.config, requirements } }
        : step
    );
    setSteps(next);
    setFieldValue("monetaryDetails.workflowSteps", next);
  }

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    })
  );

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = middleSteps.findIndex((s) => s.type === active.id);
    const newIndex = middleSteps.findIndex((s) => s.type === over.id);
    if (oldIndex < 0 || newIndex < 0) return;

    const newMiddle = arrayMove(middleSteps, oldIndex, newIndex);
    applyNewMiddleOrder(newMiddle);
  }

  const sortableItems = middleSteps.map((s) => s.type);

  const stepDescriptions: Record<WorkflowStepType, string> = {
    [WorkflowStepType.WILLINGNESS]:
      "Gathers employee consent for the transaction.",
    [WorkflowStepType.ADMIN_APPROVAL]:
      "Requires an admin to approve the request before proceeding.",
    [WorkflowStepType.DOCUMENT_SIGNING]:
      "Generates necessary documents for the employee to sign.",
    [WorkflowStepType.EXERCISE_PAYMENT]:
      "Collects payment for the exercised options.",
    [WorkflowStepType.TAX_PAYMENT]:
      "Collects applicable taxes for the exercise transaction.",
    [WorkflowStepType.SHARE_ALLOCATION]:
      "Finalizes the process by allocating shares to the employee.",
  };

  async function handleNext() {
    for (const step of steps) {
      const error = getWorkflowRequirementError(
        getWorkflowStepRequirements(steps, step.type)
      );
      if (error) {
        toast.error(
          `${step.label ?? WORKFLOW_STEP_DEFAULT_LABELS[step.type]}: ${error}`
        );
        return;
      }
    }
    try {
      await saveTransactionDetails(values);
    } catch (error: any) {
      toast.error(
        error?.response?.data?.errorRaw ||
          "Could not save the exercise configuration. Please try again."
      );
      return;
    }
    if (md.considerBlackOutDays) {
      onStepChange(3);
    } else {
      onStepChange(4);
    }
  }

  const disabledSteps = steps.filter((s) => !s.enabled);

  return (
    <VStack className="w-full">
      {/* Header */}
      <div className="pb-3 px-6 font-medium border-b-[0.5px] pt-7">
        <p className="font-semibold text-lg1 text-black-501">
          Employee Workflow Order
        </p>
        <p className="text-xs text-gray-500 font-normal mt-1">
          Define the exact sequence of steps an employee goes through when
          submitting an exercise request. Drag the handle on any step to reorder
          it. Steps marked as locked are required and will always stay in their
          fixed position — they cannot be moved or removed.
        </p>
      </div>

      {/* Steps list */}
      <VStack className="px-6 py-4 gap-2">
        {visibleSteps.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-8">
            No steps are enabled. Go back and configure payment and document
            settings.
          </p>
        ) : (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={sortableItems}
              strategy={verticalListSortingStrategy}
            >
              {visibleSteps.map((step, index) => {
                const isFixed =
                  fixedFirstSet.has(step.type) || fixedLastSet.has(step.type);
                return (
                  <SortableStepItem
                    key={step.type}
                    step={step}
                    isFixed={isFixed}
                    description={stepDescriptions[step.type]}
                    stepNumber={index + 1}
                    requirementEditor={
                      REQUIREMENT_CONFIGURABLE_STEPS.has(step.type) ? (
                        <WorkflowRequirementEditor
                          requirements={getWorkflowStepRequirements(
                            steps,
                            step.type
                          )}
                          onChange={(requirements) =>
                            updateRequirements(step.type, requirements)
                          }
                        />
                      ) : undefined
                    }
                  />
                );
              })}
            </SortableContext>
          </DndContext>
        )}

        {/* Disabled / not-included steps */}
        {disabledSteps.length > 0 && (
          <VStack className="mt-3 gap-1.5">
            <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide">
              Not included
            </p>
            {disabledSteps.map((step) => (
              <div
                key={step.type}
                className="flex items-center gap-3 border border-dashed border-gray-200 rounded-lg px-4 py-3 opacity-50"
              >
                <span className="w-6 h-6 rounded-full bg-gray-100 text-gray-400 text-xs flex items-center justify-center shrink-0">
                  —
                </span>
                <div className="w-[18px] shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-500">
                    {WORKFLOW_STEP_DEFAULT_LABELS[step.type]}
                  </p>
                  <p className="text-xs text-gray-400 mt-0.5">
                    {stepDescriptions[step.type]} — enable in the previous step
                    to include.
                  </p>
                </div>
              </div>
            ))}
          </VStack>
        )}
      </VStack>

      {/* Navigation */}
      <HStack className="justify-between px-6 pb-6 pt-2 border-t">
        <ButtonPrimary1 onClick={() => onBackClick(2)}>Back</ButtonPrimary1>
        <ButtonPrimary onClick={handleNext}>Next</ButtonPrimary>
      </HStack>
    </VStack>
  );
}
